/**
 * AdminEditorModal Component
 *
 * Modal dialog with 3 tabs for the admin visual editor feature:
 * 1. App Grid - Edit app launcher grid items (reorder, visibility, labels)
 * 2. Sidebar Phases - Configure sidebar phase organization
 * 3. Phase Names - Customize phase display names
 *
 * Features:
 * - Tab switching with visual indicators
 * - Save/Cancel buttons
 * - Load settings on open
 * - Save to server on confirm
 * - Dirty state tracking with unsaved changes warning
 *
 * @example
 * ```tsx
 * import { AdminEditorModal } from '@features/admin/components';
 *
 * function AdminPage() {
 *   const [open, setOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <button onClick={() => setOpen(true)}>Open Editor</button>
 *       <AdminEditorModal
 *         open={open}
 *         onClose={() => setOpen(false)}
 *       />
 *     </>
 *   );
 * }
 * ```
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@shared/components/ui/Modal';
import { Grid3X3, Layers, Type, Loader2, AlertTriangle } from 'lucide-react';
import { AppGridEditorTab } from './tabs/AppGridEditorTab';
import { SidebarPhaseConfigTab } from './tabs/SidebarPhaseConfigTab';
import { PhaseNameEditorTab } from './tabs/PhaseNameEditorTab';
import {
  useAdminEditorStore,
  useAdminEditorStatus,
  usePersistenceActions,
} from '../store/adminEditorStore';
import { cn } from '@/shared/utils/cn';

export interface AdminEditorModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Callback on successful save */
  onSuccess?: () => void;
}

/** Tab type for the editor */
type EditorTab = 'app-grid' | 'sidebar-phases' | 'phase-names';

/** Tab configuration */
interface TabConfig {
  id: EditorTab;
  label: string;
  icon: React.FC<{ size?: number; className?: string }>;
  component: React.FC<{ className?: string }>;
}

const TABS: TabConfig[] = [
  {
    id: 'app-grid',
    label: 'App Grid',
    icon: Grid3X3,
    component: AppGridEditorTab,
  },
  {
    id: 'sidebar-phases',
    label: 'Sidebar Phases',
    icon: Layers,
    component: SidebarPhaseConfigTab,
  },
  {
    id: 'phase-names',
    label: 'Phase Names',
    icon: Type,
    component: PhaseNameEditorTab,
  },
];

/**
 * AdminEditorModal - Visual editor for app configuration
 *
 * Features:
 * - 3 tabs: App Grid, Sidebar Phases, Phase Names
 * - Load settings from server on open
 * - Save to server on confirm
 * - Dirty state tracking
 * - Loading and error states
 */
export function AdminEditorModal({
  open,
  onClose,
  onSuccess,
}: AdminEditorModalProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('app-grid');
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Store status
  const { isLoading, error, isDirty } = useAdminEditorStatus();

  // Persistence actions
  const { loadFromServer, saveToServer, resetToDefaults } = usePersistenceActions();

  // Reset and set dirty state
  const setDirty = useAdminEditorStore((state) => state.setDirty);
  const setError = useAdminEditorStore((state) => state.setError);

  // Load settings when modal opens
  useEffect(() => {
    if (open) {
      // Reset state
      setActiveTab('app-grid');
      setShowUnsavedWarning(false);
      setError(null);

      // Load from server
      loadFromServer();
    }
  }, [open, loadFromServer, setError]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // Handle save
  const handleSave = useCallback(async () => {
    const success = await saveToServer();
    if (success) {
      onSuccess?.();
      onClose();
    }
  }, [saveToServer, onSuccess, onClose]);

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    setDirty(false);
    setShowUnsavedWarning(false);
    onClose();
  }, [setDirty, onClose]);

  // Handle cancel unsaved warning
  const handleCancelWarning = useCallback(() => {
    setShowUnsavedWarning(false);
  }, []);

  // Get active tab component
  const ActiveTabComponent = TABS.find((tab) => tab.id === activeTab)?.component;

  return (
    <>
      <Modal
        open={open && !showUnsavedWarning}
        onClose={handleClose}
        title="Admin Editor"
        size="lg"
        closeOnBackdrop={!isDirty}
      >
        <div className="space-y-4" data-testid="admin-editor-modal">
          {/* Tab Navigation */}
          <div className="flex border-b border-dark-border/30 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-150 whitespace-nowrap',
                    activeTab === tab.id
                      ? 'text-blue-400 border-b-2 border-brand-500 -mb-px'
                      : 'text-dark-muted hover:text-dark-text'
                  )}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-800/50 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="min-h-[300px]">
            {isLoading && !ActiveTabComponent ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
              </div>
            ) : (
              ActiveTabComponent && <ActiveTabComponent />
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-4 border-t border-dark-border/30">
            {/* Dirty indicator */}
            <div className="text-sm text-dark-muted">
              {isDirty ? (
                <span className="text-yellow-500">Unsaved changes</span>
              ) : (
                <span>No changes</span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input disabled:opacity-50 transition-all duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading || !isDirty}
                className={cn(
                  'flex-1 sm:flex-initial px-6 py-2 text-sm font-medium rounded-lg transition-all duration-150 flex items-center justify-center gap-2',
                  isDirty
                    ? 'bg-brand-600 hover:bg-brand-700 text-white'
                    : 'bg-dark-input text-dark-muted cursor-not-allowed'
                )}
                data-testid="save-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Unsaved Changes Warning Modal */}
      <Modal
        open={showUnsavedWarning}
        onClose={handleCancelWarning}
        title="Unsaved Changes"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="text-white/90">
                You have unsaved changes. Are you sure you want to close without
                saving?
              </p>
              <p className="text-sm text-dark-muted mt-2">
                Your changes will be lost if you discard them.
              </p>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancelWarning}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input transition-all duration-150"
            >
              Continue Editing
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-all duration-150 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save & Close'
              )}
            </button>
            <button
              type="button"
              onClick={handleDiscardChanges}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-150"
            >
              Discard Changes
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default AdminEditorModal;
